import { extractNestHttpErrorMessage } from "@shared/exceptions-message";
import { linko, type MethodResponse } from "@shared/linko";
import { useAdminEvent } from "@shared/frontend/hooks/use-admin-event";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "./UserProvider";
import { useAlert } from "./AlertProvider";

export type CallCenterWorkbench = MethodResponse<
  typeof linko.callCenter.queries.getShiftWorkbench
>;

type VolunteerTab = "task" | "caller" | "search" | "help" | "chat";

interface CallCenterShiftContextValue {
  isStation: boolean;
  stationNumber: number | null;
  workbench: CallCenterWorkbench | null;
  loading: boolean;
  tab: VolunteerTab;
  setTab: (tab: VolunteerTab) => void;
  tourOpen: boolean;
  setTourOpen: (open: boolean) => void;
  pendingFirstTour: boolean;
  setPendingFirstTour: (open: boolean) => void;
  refresh: () => Promise<void>;
  applyWorkbench: (data: CallCenterWorkbench | null) => void;
  farewell: string | null;
  setFarewell: (message: string | null) => void;
  unreadChat: number;
  setUnreadChat: (n: number | ((prev: number) => number)) => void;
}

const CallCenterShiftContext = createContext<CallCenterShiftContextValue | null>(
  null,
);

export const useCallCenterShift = () => {
  const ctx = useContext(CallCenterShiftContext);
  if (!ctx) {
    throw new Error("CallCenterShiftProvider is required");
  }
  return ctx;
};

export const CallCenterShiftProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const { setAlertText, setSeverity } = useAlert();
  const [tab, setTab] = useState<VolunteerTab>("task");
  const [tourOpen, setTourOpen] = useState(false);
  const [pendingFirstTour, setPendingFirstTour] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const [farewell, setFarewell] = useState<string | null>(null);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const shiftIdRef = useRef<string | null>(null);
  const volunteerIdRef = useRef<string | null>(null);

  const isStation = !!currentUser?.isCallCenterStation;
  const stationNumber = currentUser?.stationNumber ?? null;

  const workbenchQuery = useQuery({
    queryKey: ["call-center-workbench", currentUser?.id],
    queryFn: async () => {
      try {
        const { data } = await linko.callCenter.queries.getShiftWorkbench();
        return data;
      } catch (error) {
        if (extractNestHttpErrorMessage(error) === "אין משמרת פעילה בעמדה זו") {
          return null;
        }
        throw error;
      }
    },
    enabled: !!currentUser && isStation,
    retry: false,
    refetchInterval: (query) =>
      query.state.data || query.state.status === "error" ? 60_000 : false,
  });

  shiftIdRef.current = workbenchQuery.data?.shift.id ?? null;
  volunteerIdRef.current = workbenchQuery.data?.volunteer.id ?? null;

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["call-center-workbench"] });
  }, [queryClient]);

  const applyWorkbench = useCallback(
    (data: CallCenterWorkbench | null) => {
      shiftIdRef.current = data?.shift.id ?? null;
      volunteerIdRef.current = data?.volunteer.id ?? null;
      const queryKey = ["call-center-workbench", currentUser?.id] as const;
      void queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, data);
    },
    [queryClient, currentUser?.id],
  );

  useAdminEvent("call-center:match", (data) => {
    if (!isStation || data.shiftId !== shiftIdRef.current) return;
    setSeverity("success");
    setAlertText(data.encouragement);
    void refresh();
  });

  useAdminEvent("call-center:chat", (data) => {
    if (isStation && data.volunteerId !== volunteerIdRef.current) return;
    if (isStation && data.fromCoordinator && tabRef.current !== "chat") {
      setUnreadChat((n) => n + 1);
    }
    void queryClient.invalidateQueries({ queryKey: ["call-center-chat"] });
  });

  const value = useMemo<CallCenterShiftContextValue>(
    () => ({
      isStation,
      stationNumber,
      workbench: workbenchQuery.data ?? null,
      loading: workbenchQuery.isLoading,
      tab,
      setTab,
      tourOpen,
      setTourOpen,
      pendingFirstTour,
      setPendingFirstTour,
      refresh,
      applyWorkbench,
      farewell,
      setFarewell,
      unreadChat,
      setUnreadChat,
    }),
    [
      isStation,
      stationNumber,
      workbenchQuery.data,
      workbenchQuery.isLoading,
      tab,
      tourOpen,
      pendingFirstTour,
      refresh,
      applyWorkbench,
      farewell,
      unreadChat,
    ],
  );

  return (
    <CallCenterShiftContext.Provider value={value}>
      {children}
    </CallCenterShiftContext.Provider>
  );
};
